import {
  BookOpenText,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  MessagesSquare,
  Droplets,
  LifeBuoy,
  LogOut,
  Menu,
  NotebookPen,
  ShoppingCart,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import type { ModuleKey } from "../../shared/modules";
import { useActiveModules, visibileConModuli } from "../lib/active-modules";
import { MOBILE_MEDIA_QUERY } from "../lib/responsive";
import { trpc, type UserSession } from "../lib/trpc";
import { BrandMark } from "./BrandMark";
import { GlobalSearch } from "./GlobalSearch";

const ORDINE_BARRA_RAPIDA = ["/", "/settimana", "/interventi", "/acquisti", "/strutture"];

type AppShellProps = {
  session: UserSession;
  onLogout: () => void;
  children: ReactNode;
};

export function AppShell({ session, onLogout, children }: AppShellProps) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_MEDIA_QUERY).matches);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const canManage = session.membership.role !== "collaborator";
  const { attivi } = useActiveModules();
  // Il pallino dei messaggi non letti si chiede solo se il modulo e acceso, e mai durante un
  // accesso di assistenza: in tutti e due i casi la procedura risponderebbe un rifiuto, e un
  // rifiuto atteso non e un errore da mostrare ne una domanda da rifare ogni minuto.
  const nonLetti = trpc.chat.nonLetti.useQuery(undefined, {
    enabled: attivi.has("communications") && !session.impersonation,
    refetchInterval: 60_000,
  });
  const messaggiDaLeggere = nonLetti.data?.quanti ?? 0;
  // Le voci di un modulo spento non si mostrano affatto, nemmeno disattivate: una vetrina
  // di funzioni non comprate genera domande al cliente invece di aiutarlo a lavorare.
  // La `module` di ogni voce e la stessa chiave che guarda la guardia sul server.
  const links = visibileConModuli<{ to: string; label: string; icon: typeof CalendarDays; end?: boolean; module?: ModuleKey }>([
    { to: "/", label: "Oggi", icon: CalendarDays, end: true },
    { to: "/settimana", label: "Settimana", icon: CalendarRange, module: "weekly_schedule" },
    { to: "/interventi", label: "Interventi", icon: ClipboardList },
    { to: "/registro-ore", label: "Registro ore", icon: NotebookPen },
    { to: "/strutture", label: "Strutture", icon: Building2 },
    { to: "/piscine", label: "Piscine", icon: Droplets, module: "pool_log" },
    { to: "/acquisti", label: "Acquisti", icon: ShoppingCart },
    { to: "/comunicazioni", label: "Comunicazioni", icon: MessagesSquare, module: "communications" },
    ...(canManage ? [
      { to: "/squadra", label: "Squadra", icon: UsersRound },
      { to: "/catalogo", label: "Catalogo", icon: BookOpenText },
    ] : []),
    { to: "/supporto", label: "Supporto", icon: LifeBuoy },
  ], attivi);

  // La barra in basso ha quattro posti e li assegna per ordine dichiarato, non prendendo
  // i primi quattro del menu: cosi accendere un modulo non spinge fuori dal telefono una
  // sezione che si usa sul campo. Quando la Settimana e accesa, la struttura resta nel menu.
  const scorciatoie = ORDINE_BARRA_RAPIDA
    .map((to) => links.find((voce) => voce.to === to))
    .filter((voce): voce is NonNullable<typeof voce> => Boolean(voce))
    .slice(0, 4);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const updateLayout = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    media.addEventListener("change", updateLayout);

    return () => media.removeEventListener("change", updateLayout);
  }, []);

  useEffect(() => {
    if (!open) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      menuButtonRef.current?.focus();
    };
    window.addEventListener("keydown", closeOnEscape);

    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <div className="app-frame">
      <header className="mobile-header">
        <BrandMark />
        <button ref={menuButtonRef} className="icon-button" type="button" onClick={() => setOpen(!open)} aria-label={open ? "Chiudi menu" : "Apri menu"} aria-controls="menu-principale" aria-expanded={open}>
          {open ? <X /> : <Menu />}
        </button>
      </header>
      <aside id="menu-principale" className={`sidebar${open ? " sidebar--open" : ""}`} aria-hidden={isMobile ? !open : false} inert={isMobile && !open ? true : undefined}>
        <div className="sidebar__brand"><BrandMark /></div>
        <nav className="sidebar__nav" aria-label="Navigazione principale">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}>
              <Icon size={21} /> <span>{label}</span>
              {to === "/comunicazioni" && messaggiDaLeggere > 0 && (
                <span className="pallino-messaggi" aria-label={`${messaggiDaLeggere} da leggere`}>{messaggiDaLeggere}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__account">
          <span className="avatar">{session.user.name.slice(0, 2).toUpperCase()}</span>
          <NavLink className="sidebar__account-link" to="/profilo" onClick={() => setOpen(false)}>
            <strong>{session.user.name}</strong><small>{session.company.name}</small>
          </NavLink>
          <button className="icon-button icon-button--inverse" onClick={onLogout} type="button" aria-label="Esci"><LogOut size={19} /></button>
        </div>
      </aside>
      {open && <button type="button" className="sidebar-backdrop" aria-label="Chiudi menu" onClick={() => setOpen(false)} />}
      <div className="app-content">
        <div className="app-topbar"><GlobalSearch /></div>
        {children}
      </div>
      <nav className="bottom-nav" aria-label="Navigazione rapida">
        {scorciatoie.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}>
            <Icon size={21} /><span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
